import { Icon, LelyDialog } from '@/common/components';
import StepperBar from '../StepperBar/StepperBar';
import { StepProgress } from '@/rtk/settingsActivityTypes';
import { Box, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  RingBufferDates,
  RingBufferRequestType,
  useEmptyRingBufferMutation,
  useGetRingBufferHistoryQuery,
} from '@/rtk/ringBufferApi';
import { useLocation } from 'react-router-dom';
import RingBufferTable from './RingBufferTable';
import { useTimeConversion } from '@/common/customHooks';
import RingBufferActions from './RingBufferActions';
import RingBufferInfoSection from './RingBufferInfoSection';
import IconDivider from '../../common/components/IconDivider/IconDivider';
import './RingBuffer.scss';
import {
  generateProgressSteps,
  INITIAL_PROGRESS_STEPS,
  REQUEST_FAILED_ADVICE,
  REQUEST_FAILED_MESSAGE,
  SERVER_ERROR_MESSAGE,
} from './utils';
import RingBufferButton from './RingBufferOpenButton';

type RingBufferRequestError = {
  status?: number;
  data?: { error?: string; responseReceivedDate?: string } | string;
};

const RingBuffer = () => {
  const { toFarmLocalTime } = useTimeConversion();
  const location = useLocation();
  const entityId = location.pathname.split('/')[2];

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [stepProgress, setStepProgress] = useState<StepProgress>(
    INITIAL_PROGRESS_STEPS,
  );
  const hasSubmittedRequest = useRef(false);

  const { data, isLoading: isHistoryLoading } = useGetRingBufferHistoryQuery(
    {
      entityId,
    },
    {
      skip: !entityId || !isModalOpen,
    },
  );
  const [emptyRingBuffer, { isLoading }] = useEmptyRingBufferMutation();
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    hasSubmittedRequest.current = false;
    setStepProgress(INITIAL_PROGRESS_STEPS);
  }, []);

  useEffect(() => {
    if (!isModalOpen || !data?.length || hasSubmittedRequest.current) {
      return;
    }

    const latestRequest = data[0];
    const hasFailed = latestRequest.result === 'Failed';
    const hasKnownResult = hasFailed || latestRequest.result === 'Acknowledged';

    const steps = generateProgressSteps(
      hasKnownResult ? 2 : 1,
      hasFailed ? 1 : undefined,
      toFarmLocalTime(latestRequest.requestedDate),
      toFarmLocalTime(latestRequest.responseReceivedDate),
      hasFailed ? REQUEST_FAILED_MESSAGE : undefined,
      hasFailed ? REQUEST_FAILED_ADVICE : undefined,
    );

    if (!hasKnownResult) {
      steps.steps[1] = 'Result unavailable';
    }

    setStepProgress(steps);
  }, [data, isModalOpen, toFarmLocalTime]);

  const handleEmptyRingBuffer = useCallback(
    async (dates: RingBufferDates, requestType: RingBufferRequestType) => {
      const { fromDate, toDate, requestedDate } = dates;

      hasSubmittedRequest.current = true;
      const steps = generateProgressSteps(
        0,
        undefined,
        toFarmLocalTime(requestedDate),
      );
      setStepProgress(steps);

      await emptyRingBuffer({
        entityId,
        fromDate,
        toDate,
        requestedDate,
        requestType,
      })
        .unwrap()
        .then((response) => {
          const steps = generateProgressSteps(
            2,
            undefined,
            toFarmLocalTime(requestedDate),
            toFarmLocalTime(response.responseReceivedDate),
          );

          setStepProgress(steps);
        })
        .catch((e: RingBufferRequestError) => {
          const isServerFailure =
            e.status !== undefined && e.status >= 500 && e.status < 600;

          if (isServerFailure) {
            const endDate = new Date().toISOString();
            const responseReceivedDate =
              typeof e.data === 'object' && e.data !== null
                ? e.data.responseReceivedDate || endDate
                : endDate;

            let error = REQUEST_FAILED_MESSAGE;
            if (e.status === 500) {
              error = SERVER_ERROR_MESSAGE;
            } else if (typeof e.data === 'object' && e.data !== null) {
              error = e.data.error || REQUEST_FAILED_MESSAGE;
            }

            const steps = generateProgressSteps(
              2,
              1,
              toFarmLocalTime(requestedDate),
              toFarmLocalTime(responseReceivedDate),
              error,
              REQUEST_FAILED_ADVICE,
            );

            setStepProgress(steps);
            return;
          }

          const steps = generateProgressSteps(
            0,
            0,
            toFarmLocalTime(requestedDate),
            undefined,
            REQUEST_FAILED_MESSAGE,
            REQUEST_FAILED_ADVICE,
          );
          setStepProgress(steps);
        });
    },
    [
      entityId,
      setStepProgress,
      toFarmLocalTime,
      generateProgressSteps,
      emptyRingBuffer,
    ],
  );

  const content = (
    <Box
      className="ring-buffer"
      sx={(theme) => ({ gap: theme.lelySpacing.LG })}
    >
      <Box className="ring-buffer-disclaimer">
        <Icon name="lely-icon-information" />
        <Typography variant="xs">
          Once your request is acknowledged, it can take a few minutes for the
          data to be visible in LTDA.
        </Typography>
      </Box>

      <RingBufferInfoSection />

      <Stack sx={(theme) => ({ gap: theme.lelySpacing.SM })}>
        <RingBufferActions
          isLoading={isLoading}
          onSubmit={handleEmptyRingBuffer}
        />

        <StepperBar
          className="ring-buffer-stepper-bar"
          stepProgress={stepProgress}
        />
      </Stack>

      <Stack sx={(theme) => ({ gap: theme.lelySpacing.SM })}>
        <IconDivider icon="lely-icon-history" label="History" />

        <RingBufferTable
          data={data || []}
          isLoading={isHistoryLoading}
          onClose={handleCloseModal}
        />
      </Stack>
    </Box>
  );

  return (
    <>
      <RingBufferButton onClick={() => setIsModalOpen(true)} />

      <LelyDialog
        size="lg"
        title="Retrieve buffered data"
        content={content}
        open={isModalOpen}
        leftActionButton="Cancel"
        leftActionHandler={handleCloseModal}
        onClose={handleCloseModal}
        isLoading={isLoading}
      />
    </>
  );
};

export default RingBuffer;
